<template>
  <div
    class="flex flex-col min-h-full justify-center bg-[url('/img/bgpath.svg')] bg-[center_top_10%] bg-norepeat bg-cover]"
  >
    <div
      class="grid grid-cols-1 md:grid-cols-12 md:space-x-12 md:space-y-8 min-h-full"
    >
      <div
        v-if="canView && selectedElection"
        class="flex flex-col p-10 rounded-none lg:rounded-xl justify-start lg:justify-center items-center lg:items-start col-span-5 relative"
      >
        <a
          class="lg:absolute lg:top-12 lg:left-10 flex flex-row items-center gap-2 text-xs mb-10 mr-auto text-truegray-500 hover:text-tertiary"
          @click="backToElectionSchedule"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15.75 19.5 8.25 12l7.5-7.5"
            />
          </svg>
          Back to election schedule
        </a>
        <div class="">
          <h5
            :class="
              selectedElection
                ? 'bg-elections-tertiary p-1 w-[max-content] text-base mx-auto lg:mx-0'
                : 'text-elections-primary-600 text-4xl'
            "
            class="text-center lg:text-left mb-2 transition-all"
          >
            {{ school?.name }}
          </h5>
          <h4
            v-if="selectedElection"
            class="text-elections-primary-600 text-center lg:text-left"
          >
            {{ selectedElection.election_name }}
            <span class="text-secondary bg-elections-primary-600 font-bold px-2">Voting</span>
          </h4>
        </div>

        <div v-if="canVote" class="">
          <p class="my-4 text-sm text-center lg:text-left text-truegray-500">
            Hi <span class="font-bold text-elections-primary-600">{{ voterName }}.</span>
          </p>
          <p class="my-4 text-sm text-center lg:text-left text-truegray-500">
            <span class="font-bold text-green-500">
              You are eligible to vote in this election for up to
              {{ selectedElection.min_candidates }} candidates.
            </span>
            <br />
            <span>
              Please select up to {{ selectedElection.min_candidates }} and
              submit your vote. You may vote for less than the maximum if you
              choose.
            </span>
          </p>

          <button
            type="button"
            class="text-center mx-auto md:hidden"
            @click="scrollToCandidatesSection"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-8 h-8 text-tertiary hover:text-elections-primary-600"
            >
              <path
                fill-rule="evenodd"
                d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          <div
            v-if="selectedCandidates.length"
            class="hidden md:flex w-full justify-center lg:justify-start"
          >
            <ModalVoting />
          </div>
        </div>

        <div v-if="hasVoted" class="">
          <p class="my-4 text-sm text-center lg:text-left text-truegray-500">
            Hi <span class="font-bold text-elections-primary-600">{{ voterName }}.</span>
          </p>
          <p class="my-4 text-sm text-center lg:text-left text-truegray-500">
            <span class="font-bold text-red-500">
              Your vote has already been cast </span
            >.
          </p>
        </div>
      </div>
      <div
        v-if="canView && selectedElection?.candidates"
        ref="candidatesSection"
        :class="[
          'flex flex-col gap-4 col-span-12 md:col-span-7 items-center justify-center',
          canVote ? 'opacity-100' : 'opacity-50',
        ]"
      >
        <ListCandidates
          :candidates="selectedElection.candidates"
          :is-voting="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import ListCandidates from '../../../components/elections/ListCandidates.vue'
import ModalVoting from '../../../components/modal/ModalVoting.vue'

export default defineComponent({
  name: 'ElectionVoting',
  components: { ListCandidates, ModalVoting },

  setup() {
    const route = useRoute()
    const router = useRouter()

    const store = useStore()

    const canView = computed(() => store.getters['elections/canView'])

    const canVote = computed(() => store.getters['elections/canVote'])

    const hasVoted = computed(() => store.getters['elections/hasVoted'])

    const voterName = computed(() => store.getters['elections/getVoterName'])

    const school = computed(() => store.getters['elections/getSchool'] || null)

    const selectedCandidates = computed(
      () => store.getters['elections/getSelectedCandidates'],
    )

    const remainingCandidateSelections = computed(
      () => store.getters['elections/remainingCandidateSelections'],
    )

    const selectedElection = computed(
      () => store.getters['elections/getCurrentElection'],
    )

    const candidatesSection = ref(null)
    const scrollToCandidatesSection = () => {
      if (candidatesSection.value) {
        candidatesSection.value.scrollIntoView({ behavior: 'smooth' })
      }
    }

    const watchElectionId = async () => {
      let electionId = route.params.electionId
      
      // TEMPORARY FIX: Handle malformed voting URLs from printed QR codes
      // When special characters in school slugs get corrupted (e.g. kōwhai → k?whai),
      // the ? breaks URL parsing and electionId becomes empty
      // TODO: Remove once all printed materials expire
      if (!electionId) {
        const fullUrl = window.location.href
        const match = fullUrl.match(/\/(\d+)\/vote/)
        if (match) {
          electionId = match[1]
          // DEBUG: Uncomment for troubleshooting malformed URLs
          // eslint-disable-next-line no-console
          // console.log('ElectionVoting: Extracted election ID from malformed voting URL:', electionId)
        }
      }
      
      if (electionId) {
        await store.dispatch(
          'elections/dispatchUpdateCurrentElection',
          electionId,
        )
      } else {
        store.dispatch('elections/dispatchUpdateCurrentElection')
      }
    }

    const backToElectionSchedule = () => {
      router.push({
        name: 'schoolElection',
        params: {
          clientSlug: route.params.clientSlug,
          electionId: route.params.electionId,
        },
      })
    }

    onMounted(async () => {
      // DEBUG: Uncomment for troubleshooting malformed URLs
      // eslint-disable-next-line no-console
      // console.log('=== ElectionVoting onMounted DEBUG ===')
      // eslint-disable-next-line no-console
      // console.log('Full URL:', window.location.href)
      // eslint-disable-next-line no-console
      // console.log('Route params:', route.params)
      // eslint-disable-next-line no-console
      // console.log('Route query:', route.query)
      // eslint-disable-next-line no-console
      // console.log('URL search params:', window.location.search)
      
      await watchElectionId()
      let electionId = route.params.electionId
      
      // TEMPORARY FIX: Extract election ID from malformed voting URLs (printed QR codes)
      // Same logic as in watchElectionId - needed here for vote authorization
      // TODO: Remove once all printed materials expire
      if (!electionId) {
        const fullUrl = window.location.href
        const match = fullUrl.match(/\/(\d+)\/vote/)
        if (match) {
          electionId = match[1]
          // DEBUG: Uncomment for troubleshooting malformed URLs
          // eslint-disable-next-line no-console
          // console.log('MALFORMED URL: Extracted electionId from voting URL:', electionId)
        } else {
          // DEBUG: Uncomment for troubleshooting malformed URLs
          // eslint-disable-next-line no-console
          // console.log('MALFORMED URL: No voting URL pattern found')
        }
      }
      
      // TEMPORARY FIX: Extract voting token from malformed query parameters
      // When URLs contain ? in the slug, voting token ends up in weird query structure
      // e.g. route.query = {"ap/116/vote?votingToken": "token_value"}
      // TODO: Remove once all printed materials expire
      let votingToken = route.query.votingToken
      if (!votingToken) {
        // Check if token is in the malformed query structure
        const queryKeys = Object.keys(route.query)
        const malformedKey = queryKeys.find(key => key.includes('votingToken'))
        if (malformedKey) {
          votingToken = route.query[malformedKey]
          // DEBUG: Uncomment for troubleshooting malformed URLs
          // eslint-disable-next-line no-console
          // console.log('MALFORMED URL: Extracted votingToken from malformed query key:', malformedKey, '=', votingToken)
        } else {
          // Fallback to URLSearchParams (though this likely won't work for malformed URLs)
          const urlParams = new URLSearchParams(window.location.search)
          votingToken = urlParams.get('votingToken')
          // DEBUG: Uncomment for troubleshooting malformed URLs
          // eslint-disable-next-line no-console
          // console.log('MALFORMED URL: Extracted votingToken from URLSearchParams:', votingToken)
        }
      }
      
      // DEBUG: Uncomment for troubleshooting malformed URLs
      // eslint-disable-next-line no-console
      // console.log('Final values - electionId:', electionId, 'votingToken:', votingToken)
      
      if (electionId && votingToken) {
        const payload = {
          electionId,
          votingToken,
        }

        const response = await store.dispatch(
          'elections/dispatchAuthoriseVoter',
          payload,
        )
        if (!response) {
          backToElectionSchedule()
          alert(
            'Incorrect voting token. You have been timed out. Please use the link in your email and try again later.',
          )
        }
      } else {
        backToElectionSchedule()
        alert('Missing election ID or voting token.')
      }
    })

    return {
      canView,
      selectedElection,
      school,
      voterName,
      canVote,
      hasVoted,
      candidatesSection,
      scrollToCandidatesSection,
      backToElectionSchedule,
      selectedCandidates,
      remainingCandidateSelections,
    }
  },
})
</script>
